import { useCallback, useState } from "react";
import type { ImageMetaData } from "../util/imageMetadata";
import { extractMetadataFromImage } from "../util/imageMetadata";

interface ImageMetadataItem {
  url?: string;
  uid?: string;
  alt?: string;
}

interface UseImageMetadataProps {
  images: ImageMetadataItem[];
}
let attempts = 0;
const maxAttempts = 3;

export const useImageMetaData = ({ images }: UseImageMetadataProps) => {
  const [metadata, setMetadata] = useState<Record<string, ImageMetaData>>({});
  const [loading, setLoading] = useState(false);
  const fetchMetadata = useCallback(async () => {
    setLoading(true);
    try {
      if (!images?.length) return;

      const metadataPromises = images.map(async (img) => {
        if (!img.url) return null;
        const meta = await extractMetadataFromImage(img.url);
        return meta ? { [img.url]: meta } : null;
      });

      const results = await Promise.all(metadataPromises);

      const meta = results.reduce<Record<string, ImageMetaData>>(
        (acc, curr) => {
          if (curr) {
            for (const key in curr) {
              acc[key] = curr[key];
            }
          }
          return acc;
        },
        {},
      );

      if (meta) {
        setMetadata(meta);
      }

      // Check if all images have alt texts
      const allImagesHaveAltTexts = images.every(
        (img) => img.alt || (img.url && meta?.[img.url]?.customMetadata?.alt),
      );

      // If not all images have alt texts and we haven't reached max attempts, retry
      if (!allImagesHaveAltTexts && attempts < maxAttempts) {
        attempts++;
        setTimeout(fetchMetadata, 1000);
      }
    } catch (error) {
      console.error("Error fetching metadata", error);
    } finally {
      setLoading(false);
    }
  }, [images]);

  return { metadata, fetchMetadata, loading };
};
